/* eslint-disable max-lines */
import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { combineLatest, Subject } from 'rxjs';
import { shareReplay, takeUntil } from 'rxjs/operators';

import type {
  Addon,
  BillingPeriod,
  BillingPlan,
  Currency,
  PaymentInterval,
  PricingCompanySize,
  Subscription,
} from '@core/billing-types';
import { BillingPlanTier, PricingCompanySizeE } from '@core/billing-types';
import { filterBoolean } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import { SpamVerificationService } from '@core/spam-verification-logic';
import {
  SpamVerificationDialogService,
  SpamVerificationInfoboxContainerComponent,
} from '@core/spam-verification-smart-ui';
import {
  BillingPlansDataService,
  getQuantityForCompanySize,
  SubscriptionDataService,
} from '@recruitee/billing-logic';
import { getPricingCompanySize } from '@recruitee/billing-util';

import { get2024ProductCosts } from '../../../helpers/get-2024-product-costs';
import { getProductName } from '../../../helpers/get-product-name';
import { ready } from '../../../helpers/rx-operators';
import { ProductOrderPersistenceService } from '../../../services/product-order-persistence.service';
import type { PricingFeatureLoadedModel } from '../../../types/pricing-feature.types';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../../types/pricing-feature.types';
import { AssignedProductMessageContainerComponent } from '../../assigned-product-message/assigned-product-message-container.component';
import { getInitialChargebeeInterval } from '../lib/get-initial-interval';
import { getPlanPicker2024ProductStatus } from '../lib/get-plan-picker-2024-product-status';
import { isCustomBillingPeriod } from '../lib/plan-picker';
import { PlanPickerAnalytics } from '../plan-picker.analytics';
import { PlanPicker2024CompareComponent } from './compare/plan-picker-2024-compare.component';
import { PlanPicker2024FooterComponent } from './footer/plan-picker-2024-footer.component';
import type { Product, Products } from './plan-picker-2024.types';
import { ViewMode } from './plan-picker-2024.types';
import { PlanPicker2024ProductsComponent } from './products/plan-picker-2024-products.component';
import { SizeSelectMode } from './toolbar/plan-picker-2024-toolbar.component';

type PlanPickerUIData = [
  BillingPlan[],
  BillingPlan | undefined,
  PricingFeatureLoadedModel,
  Subscription,
];

@Component({
  selector: 'rt-plan-picker-2024',
  templateUrl: './plan-picker-2024.component.html',
  styleUrls: ['./plan-picker-2024.component.less'],
  providers: [PlanPickerAnalytics],
  imports: [
    AssignedProductMessageContainerComponent,
    SpamVerificationInfoboxContainerComponent,
    PlanPicker2024ProductsComponent,
    PlanPicker2024CompareComponent,
    PlanPicker2024FooterComponent,
    TranslatePipe,
  ],
})
export class PlanPicker2024Component {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly intercomService = inject(IntercomService);
  private readonly featureModel = inject(PricingFeatureObservableModel);
  private readonly spamVerificationService = inject(SpamVerificationService);
  private readonly spamVerificationDialogService = inject(SpamVerificationDialogService);
  private readonly persistenceService = inject(ProductOrderPersistenceService);
  private readonly billingPlansData = inject(BillingPlansDataService);
  private readonly subscriptionData = inject(SubscriptionDataService);

  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly facade = inject(PricingFeatureFacade);
  public readonly i18n = inject(I18nService);
  public readonly router = inject(Router);
  public readonly location = inject(Location);
  public readonly route = inject(ActivatedRoute);
  public readonly analytics = inject(PlanPickerAnalytics);

  public readonly ViewMode = ViewMode;
  public readonly BillingPlanTier = BillingPlanTier;

  public viewMode = ViewMode.Default;
  public companySize: PricingCompanySize = PricingCompanySizeE['1-20'];
  public minimalCompanySize: PricingCompanySize | null | undefined = undefined;
  public sizeSelectMode: SizeSelectMode = SizeSelectMode.Default;
  public currency: Currency;
  public interval: PaymentInterval;
  public activePlan: BillingPlan | undefined;
  public products: Products;
  public billingPlans: BillingPlan[];
  public isSelfService: boolean;
  public isSubscriptionManual: boolean;
  public showCurrencyControl: boolean;
  public showIntervalControl: boolean;
  public showSlotsLimitMessage = false;
  public startPlanSlotsLimit: number;
  public isTrial = false;
  public activeAddons: Addon[] = [];
  public heading = '';
  public subheading = '';
  public isPP24 = false;

  private planQuantity: number | undefined;
  private jobSlotsUsed: number;
  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    this.facade.fetchPlansIfNeeded();
  }

  public ngOnInit() {
    combineLatest([
      this.billingPlansData.collection$,
      this.billingPlansData.activePlan$,
      this.featureModel.pipe(ready, shareReplay(1)),
      this.subscriptionData.data$,
    ])
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(data => this.update(data));
  }

  public update([plans, activePlan, model, subscription]: PlanPickerUIData) {
    this.billingPlans = plans;
    this.activePlan = activePlan;
    this.isTrial = !!model.trial;
    this.isSubscriptionManual = !!model.subscription?.manual;
    this.jobSlotsUsed = model.jobSlotsUsed;
    this.activeAddons = subscription?.addons || [];
    this.planQuantity = model.subscription?.planQuantity ?? undefined;
    this.isPP24 = model.isPP24;
    this.setViewMode(model, activePlan);
    this.isSelfService = this.getIsSelfService(model);
    this.setCurrency(model);
    this.setInterval(model, activePlan);
    this.setCompanySize(subscription);
    this.setControlsVisibility(model);
    this.setProducts();
    this.verifyStartPlanSlots();
    this.setHeadings();

    this.cdRef.detectChanges();
  }

  public changeCompanySize(size: PricingCompanySize) {
    this.companySize = size;
    this.setProducts();
  }

  public changeInterval(interval: PaymentInterval) {
    this.interval = interval;
    this.setProducts();
  }

  public changeCurrency(currency: Currency) {
    this.currency = currency;
    this.setProducts();
  }

  public chooseProduct(product: Product) {
    this.contactSalesAboutProduct(product.plan?.tier || BillingPlanTier.Optimize);
  }

  public cancelSubscription() {
    if (this.isSubscriptionManual) {
      this.contactSalesAboutChurn();
    } else {
      this.router.navigateByUrl(this.facade.getSubscriptionChurnPageUrl().toString());
    }
  }

  public contactSalesAboutChurn() {
    this.intercomService.startNewMessage(
      this.i18n.translate('app.pricing.plan_picker.intercom.churn'),
    );
  }

  public contactSalesAboutProduct(codename: BillingPlanTier) {
    const message =
      this.viewMode === ViewMode.StaffingAgency
        ? this.i18n.translate('app.intercom_message.optimize_for_agency')
        : this.i18n.translate('app.pricing.plan_picker.intercom.interested_in', {
            planName: getProductName(
              {
                codename,
              },
              this.i18n,
              true,
            ),
          });
    this.intercomService.startNewMessage(message);
  }

  private setViewMode(model: PricingFeatureLoadedModel, activePlan: BillingPlan | undefined) {
    this.viewMode = model.isAgency
      ? ViewMode.StaffingAgency
      : activePlan && !model.isPP24
        ? ViewMode.Migration
        : ViewMode.Default;
  }

  private getIsSelfService(model: PricingFeatureLoadedModel) {
    if (model.externalBilling) return false;
    if (!model.subscription) return true;
    if (model.subscription.manual) return true;
    return model.subscription.autoCollection || !model.subscription.manual;
  }

  private setCurrency(model: PricingFeatureLoadedModel) {
    const persistenceData = this.persistenceService.read();
    this.currency = persistenceData?.currency ?? model.currency;
  }

  private setInterval(model: PricingFeatureLoadedModel, activePlan: BillingPlan | undefined) {
    const persistenceData = this.persistenceService.read();
    this.interval =
      persistenceData?.interval ?? getInitialChargebeeInterval(model.subscription!, activePlan);
  }

  private setControlsVisibility(model: PricingFeatureLoadedModel) {
    this.showCurrencyControl = this.isSelfService && !model.currencyLock;
    this.showIntervalControl =
      this.isSelfService && !isCustomBillingPeriod(<BillingPeriod>this.interval);
  }

  private setProducts() {
    const startPlan = this.billingPlans.find(
      plan => plan.tier === BillingPlanTier.Start && plan.currency === this.currency,
    );
    const growPlan = this.billingPlans.find(
      plan => plan.tier === BillingPlanTier.Grow && plan.currency === this.currency,
    );
    const optimizePlan = this.billingPlans.find(
      plan => plan.tier === BillingPlanTier.Optimize && plan.currency === this.currency,
    );
    const plansWithPrices = this.companySize !== PricingCompanySizeE['151+'];

    this.products = {
      [BillingPlanTier.Start]: plansWithPrices
        ? this.buildProduct(startPlan)
        : this.buildProductWithoutPrices(startPlan),
      [BillingPlanTier.Grow]: plansWithPrices
        ? this.buildProduct(growPlan)
        : this.buildProductWithoutPrices(growPlan),
      [BillingPlanTier.Optimize]: this.buildProductWithoutPrices(optimizePlan),
    };
    this.cdRef.detectChanges();
  }

  private buildProduct(plan: BillingPlan | undefined) {
    const rangeStartForCompanySize = getQuantityForCompanySize(this.companySize);
    const targetBillingPeriod = this.interval;
    const planWithBillingPeriod = plan
      ? { ...plan, billingPeriod: targetBillingPeriod }
      : undefined;
    const isStatusSelfService =
      this.sizeSelectMode === SizeSelectMode.LockedMinimalMissing ? false : this.isSelfService;
    return {
      plan: plan,
      costPerMonth: get2024ProductCosts(
        plan?.prices || [],
        this.activeAddons,
        rangeStartForCompanySize,
        1,
      ),
      costPerYear: get2024ProductCosts(
        plan?.prices || [],
        this.activeAddons,
        rangeStartForCompanySize,
        12,
      ),
      status: getPlanPicker2024ProductStatus(
        isStatusSelfService,
        this.activePlan,
        planWithBillingPeriod,
        this.companySize,
      ),
    };
  }

  private buildProductWithoutPrices(plan: BillingPlan | undefined) {
    const targetBillingPeriod = this.interval;
    const planWithBillingPeriod = plan
      ? { ...plan, billingPeriod: targetBillingPeriod }
      : undefined;
    const isStatusSelfService =
      this.sizeSelectMode === SizeSelectMode.LockedMinimalMissing ? false : this.isSelfService;
    return {
      plan: plan,
      costPerMonth: null,
      costPerYear: null,
      status: getPlanPicker2024ProductStatus(
        isStatusSelfService,
        this.activePlan,
        planWithBillingPeriod,
        this.companySize,
      ),
    };
  }

  private setCompanySize(subscription?: Subscription) {
    const persistenceSize: string | undefined = this.persistenceService.read()?.companySize;
    const { minimalCompanySize } = subscription || {};
    const selectedCompanySize = getPricingCompanySize(subscription?.selectedCompanySize);
    this.minimalCompanySize = minimalCompanySize ?? null;

    if (this.viewMode === ViewMode.StaffingAgency) {
      this.sizeSelectMode = SizeSelectMode.Agency;
      this.companySize = PricingCompanySizeE['1-20'];
      return;
    }

    if (selectedCompanySize) {
      this.companySize = selectedCompanySize;
      this.sizeSelectMode = SizeSelectMode.Locked;
      return;
    }

    if (!minimalCompanySize) {
      this.companySize = PricingCompanySizeE['1-20'];
      this.sizeSelectMode = SizeSelectMode.LockedMinimalMissing;
      return;
    }

    const isMinimalSizeInDefaults = Object.values(PricingCompanySizeE).includes(
      <PricingCompanySizeE>minimalCompanySize,
    );
    this.companySize = isMinimalSizeInDefaults
      ? persistenceSize || minimalCompanySize
      : PricingCompanySizeE['151+'];
    this.sizeSelectMode = SizeSelectMode.Default;
  }

  private verifyStartPlanSlots(jobSlotsUsed: number = this.jobSlotsUsed) {
    const startPlanSlotsCount = this.products[BillingPlanTier.Start].plan!.baseSlots;
    this.startPlanSlotsLimit = startPlanSlotsCount;
    this.showSlotsLimitMessage = this.isPP24 ? false : jobSlotsUsed > startPlanSlotsCount;
  }

  private setHeadings() {
    if (this.viewMode === ViewMode.Migration) {
      const { tier, custom } = this.activePlan!;
      this.heading = this.i18n.translate('app.pricing.plan_picker_2024.heading_migration');
      let subheading = this.i18n.translate(
        'app.pricing.plan_picker_2024.subheading_migration_common',
        {
          planName: getProductName({ codename: tier, custom }, this.i18n, true),
        },
      );
      if (!this.isSubscriptionManual) {
        subheading =
          subheading +
          '<br>' +
          this.i18n.translate('app.pricing.plan_picker_2024.subheading_migration_old_plans', {
            link: this.location.prepareExternalUrl(
              this.router.serializeUrl(this.facade.getPlanPickerUrl({ legacyPlans: true })),
            ),
          });
      }
      this.subheading = subheading;
    } else {
      this.heading = this.i18n.translate('app.pricing.plan_picker_2024.heading');
      this.subheading = this.i18n.translate('app.pricing.plan_picker_2024.subheading_v2');
    }
  }
}
